import {
  extractEntityIdFromDataId,
  extractNumericEntityIdFromDataId,
  getDataPackageApiId,
  isSampleLikeNodeType,
} from './treeUtils';
import { NodeType, NodeDescriptor, LoadContext, NodeTypeConfigMap } from '@/types/tree';
import {
  SampleDto,
  SampleActivityDto,
  ProjectDto,
  DataPackageDto,
  PublicationDto,
  ProjectActivityDto,
  ShippingDto,
  CommunicationDto,
  UserSettingsDto,
  TheoryActivityDto,
  StatusDto,
  ActivityCategoryDto,
  ActivityTypeDto,
  NoteDto,
  FileMetaDataDto,
} from '@/api/ApiClient';
import { IBackend } from '@/api/BackendApi';
import { formatDate } from '@/lib/utils/dateUtils';
import { getActivityCategoryDisplay, getActivityTypeDisplay } from '@/lib/types/activityTypes';

import { isExternalProject, showsSamples, showsSimulations } from '@/lib/types/projectClassification';
import { DATA_ROUTE_ROOT_MODE_KEY } from '@/lib/tree/dataRouteTreeConstants';

/**
 * Tri-state expand hint from sample DTOs: `hasActivities === false` → not expandable;
 * `true` → expandable; omitted only when the API did not populate the flag (assume expandable).
 * Note: C# bool defaults serialize as false — list endpoints must set HasActivities explicitly (project/shipping sample lists).
 */
function sampleInitialExpandable(data: SampleDto): boolean {
  return data.hasActivities !== false;
}

/**
 * Expand if split has pieces, or processing activities that can carry in-situ CHARs have them.
 * Shared across live + RO activity shells so expandability does not drift.
 */
function sampleActivityInitialExpandable(data: SampleActivityDto): boolean {
  if (data.type === ActivityTypeDto.Dicing && (data.pieces?.length ?? 0) > 0) {
    return true;
  }
  if (!(data.inSituCharacterizations && data.inSituCharacterizations.length > 0)) {
    return false;
  }
  return (
    data.type === ActivityTypeDto.Synthesis ||
    data.type === ActivityTypeDto.Preparation ||
    data.type === ActivityTypeDto.PostTreatment
  );
}

/** Activity tree label body (no pending marker, no date suffix). */
function sampleActivityLabelBody(activity: SampleActivityDto): string {
  switch (activity.category) {
    case ActivityCategoryDto.Characterization:
      return activity.instrument || activity.characterizationTechnique || `Characterization ${activity.id}`;
    case ActivityCategoryDto.Processing:
      switch (activity.type) {
        case ActivityTypeDto.Dicing:
          return `Split - ${activity.pieces!.length} pieces`;
        case ActivityTypeDto.Preparation:
          return activity.instrument ? `Pre Growth (${activity.instrument})` : `Preparation`;
        case ActivityTypeDto.Synthesis:
          return activity.technique || `Synthesis ${activity.id}`;
        case ActivityTypeDto.PostTreatment:
          return activity.technique ? `Post Growth (${activity.technique})` : `Post Growth`;
        default:
          return getActivityTypeDisplay(activity.type);
      }
    case ActivityCategoryDto.Storage:
      return activity.sampleLocation ? `Storage - ${activity.sampleLocation}` : `Storage ${activity.id}`;
    default:
      return getActivityCategoryDisplay(activity.category);
  }
}

function sampleActivityGetLabel(activity: SampleActivityDto, showPendingMarker: boolean): string {
  const pendingMarker = showPendingMarker && activity.status === StatusDto.Pending ? '* ' : '';
  const datePart = activity.date ? new Date(activity.date).toLocaleDateString() : 'No date';
  return `${pendingMarker}${sampleActivityLabelBody(activity)} - ${datePart}`;
}

function sampleActivityGetTypeLabel(activity?: SampleActivityDto): string {
  return activity ? getActivityCategoryDisplay(activity.category) : 'Activity';
}

async function loadSampleActivityNotes(dataId: string, backend: IBackend) {
  return backend.sampleActivity.getNotes(extractNumericEntityIdFromDataId(dataId));
}

async function loadSampleActivityFiles(dataId: string, backend: IBackend) {
  return backend.sampleActivity.getFileGroups(extractNumericEntityIdFromDataId(dataId));
}

type SampleActivityChildSampleType = 'sample' | 'sampleInPackage' | 'sampleSnapshot' | 'sampleAggregate';
type SampleActivityChildActivityType =
  'sampleActivity' | 'sampleActivityInPackage' | 'sampleActivitySnapshot' | 'sampleActivityAggregate';

/** Live / aggregate / in-package pieces: `activity.pieces` are sample labels resolved via `/samples`. */
async function loadLiveSamplePiecesAs(
  activity: SampleActivityDto,
  backend: IBackend,
  childSampleType: SampleActivityChildSampleType
): Promise<NodeDescriptor<NodeType>[]> {
  if (!activity.pieces?.length) return [];
  return Promise.all(
    activity.pieces.map(async (pieceId) => {
      const sample = await backend.sample.get(pieceId);
      return { type: childSampleType, data: sample };
    })
  );
}

/** Snapshot-package pieces: resolve package-scoped SampleSnapshot rows (numeric ids). */
async function loadSnapshotSamplePieces(
  activity: SampleActivityDto,
  backend: IBackend
): Promise<NodeDescriptor<NodeType>[]> {
  if (!activity.pieces?.length) return [];
  if (activity.id === undefined) throw new Error('Missing id for activity');
  const pieces = await backend.sampleActivity.getPieces(activity.id, null, null);
  const byLabel = new Map(pieces.filter((p) => p.sampleLabel).map((p) => [p.sampleLabel as string, p]));
  return activity.pieces.map((label) => {
    const sample = byLabel.get(label);
    if (!sample) {
      throw new Error(`Missing snapshot piece for label ${label}`);
    }
    return { type: 'sampleSnapshot' as NodeType, data: sample };
  });
}

async function loadSampleActivityChildren(
  activity: SampleActivityDto,
  backend: IBackend,
  options: {
    childSampleType: SampleActivityChildSampleType;
    childActivityType: SampleActivityChildActivityType;
    pieces: 'liveByLabel' | 'snapshot';
  }
): Promise<NodeDescriptor<NodeType>[]> {
  if (activity.type === ActivityTypeDto.Dicing && activity.pieces?.length) {
    return options.pieces === 'snapshot'
      ? loadSnapshotSamplePieces(activity, backend)
      : loadLiveSamplePiecesAs(activity, backend, options.childSampleType);
  }
  if (activity.inSituCharacterizations?.length) {
    return activity.inSituCharacterizations.map((child) => ({
      type: options.childActivityType,
      data: child,
    }));
  }
  return [];
}

/** Shared shell for live + RO sample-activity node configs (label / expand / notes / files / children). */
function buildSampleActivityConfig(options: {
  isReadOnly?: boolean;
  showPendingMarker: boolean;
  childSampleType: SampleActivityChildSampleType;
  childActivityType: SampleActivityChildActivityType;
  pieces: 'liveByLabel' | 'snapshot';
}) {
  return {
    calculateInitialExpandable: sampleActivityInitialExpandable,
    canBeSelected: true as const,
    isReadOnly: options.isReadOnly,
    canHaveNotes: true as const,
    canHaveFiles: true as const,
    getTypeLabel: sampleActivityGetTypeLabel,
    getLabel: (activity: SampleActivityDto) => sampleActivityGetLabel(activity, options.showPendingMarker),
    loadChildren: async (_dataId: string, activity: SampleActivityDto, backend: IBackend, _context: LoadContext) =>
      loadSampleActivityChildren(activity, backend, {
        childSampleType: options.childSampleType,
        childActivityType: options.childActivityType,
        pieces: options.pieces,
      }),
    loadNotes: loadSampleActivityNotes,
    loadFiles: loadSampleActivityFiles,
  };
}

function readOnlySampleLabel(data: SampleDto): string {
  return data.sampleLabel || `Sample ${data.id}`;
}

async function loadSampleNotesViaSampleApi(dataId: string, backend: IBackend) {
  return backend.sample.getNotes(extractEntityIdFromDataId(dataId));
}

async function loadSampleFilesViaSampleApi(dataId: string, backend: IBackend) {
  return backend.sample.getFileGroups(extractEntityIdFromDataId(dataId));
}

/** Shared shell for RO sample nodes (aggregate / in-package / snapshot); loadChildren + APIs vary. */
function buildReadOnlySampleConfig(options: {
  typeLabel: string;
  getLabel?: (data: SampleDto) => string;
  loadChildren: (
    dataId: string,
    data: SampleDto,
    backend: IBackend,
    context: LoadContext
  ) => Promise<NodeDescriptor<NodeType>[]>;
  loadNotes: (dataId: string, backend: IBackend) => Promise<NoteDto[]>;
  loadFiles: (dataId: string, backend: IBackend) => Promise<FileMetaDataDto[]>;
}) {
  return {
    calculateInitialExpandable: sampleInitialExpandable,
    canBeSelected: true as const,
    isReadOnly: true as const,
    canHaveNotes: true as const,
    canHaveFiles: true as const,
    getLabel: options.getLabel ?? readOnlySampleLabel,
    getTypeLabel: () => options.typeLabel,
    loadChildren: options.loadChildren,
    loadNotes: options.loadNotes,
    loadFiles: options.loadFiles,
  };
}

/** Map activities to child descriptors; every activity must have an id. */
function mapActivitiesToDescriptors(
  activities: SampleActivityDto[],
  childType: SampleActivityChildActivityType
): NodeDescriptor<NodeType>[] {
  return activities.map((activity) => {
    if (activity.id === undefined) throw new Error('Missing id for activity');
    return { type: childType, data: activity };
  });
}

/**
 * Growth-run tree children shared by live samples and published aggregates.
 * Multi-sample children keep synthesis on the growth-run row; merge parent activities when the sample
 * is not nested under another sample node. Growth-run roots also load sub-samples.
 */
async function loadGrowthAwareSampleChildren(
  dataId: string,
  data: SampleDto,
  backend: IBackend,
  context: LoadContext,
  options: {
    activityChildType: SampleActivityChildActivityType;
    sampleChildType: SampleActivityChildSampleType;
    activitiesRouteKey: string;
  }
): Promise<NodeDescriptor<NodeType>[]> {
  const activities = await backend.sample.getActivities(options.activitiesRouteKey, undefined, undefined);

  // Nested under a sample in the tree: parent already shows Prep/Syn — do not re-merge.
  const isNestedUnderSample = isSampleLikeNodeType(context.parentType);
  if (!isNestedUnderSample && data.parentId) {
    // Snapshot parent ids are SampleSnapshot PKs; /samples/{id} prefers live and can collide.
    const growthActivities =
      data.isSnapshot === true
        ? await backend.sampleSnapshot.getActivities(data.parentId, undefined, undefined)
        : await backend.sample.getActivities(data.parentId.toString(), undefined, undefined);
    activities.push(...growthActivities);
  }

  const growthRunKey = data.sampleLabel ?? extractEntityIdFromDataId(dataId);
  const samples = data.isGrowthRun ? await backend.sample.getSamplesOnGrowthRun(growthRunKey, null, null) : [];

  return [
    ...mapActivitiesToDescriptors(activities, options.activityChildType),
    ...samples.map((sample) => ({ type: options.sampleChildType, data: sample })),
  ];
}

/**
 * Canonical `/data` browse roots that start from a {@link ProjectDto}: list publication milestones as
 * `publicationInData` shells, each expandable into linked datasets. Data packages belonging to the project
 * but not returned as linked datasets for any of those publications are still listed directly under the project
 * so nothing disappears from the flattened view.
 */
async function loadDescriptorsForCanonicalDataBrowseProject(projectId: string, backend: IBackend) {
  const [activities, allProjectPackages] = await Promise.all([
    backend.project.getProjectActivities(projectId),
    backend.project.getDataPackages(projectId, false),
  ]);

  const seenPublicationIds = new Set<number>();
  const publicationActivities = activities.filter((a) => {
    if (a.type !== ActivityTypeDto.Publication || a.publication?.id === undefined || a.publication.id === null)
      return false;
    const id = a.publication.id as number;
    if (seenPublicationIds.has(id)) return false;
    seenPublicationIds.add(id);
    return true;
  });

  publicationActivities.sort((a, b) => {
    const la = publicationInDataDescriptorsSortKey(a.publication as PublicationDto);
    const lb = publicationInDataDescriptorsSortKey(b.publication as PublicationDto);
    return la.localeCompare(lb, undefined, { sensitivity: 'base' });
  });

  const publicationDescriptors = publicationActivities.map((a) => ({
    type: 'publicationInData' as NodeType,
    data: a.publication as PublicationDto,
    isFullyLoaded: false,
  }));

  const coveredPackageIds = new Set<number>();
  await Promise.all(
    publicationDescriptors.map(async ({ data: publication }) => {
      const idOrDoi = publication.doi?.trim() || String(publication.id);
      const pkgs = await backend.publication.getDataPackagesByIdOrDoi(idOrDoi);
      for (const dp of pkgs) {
        if (typeof dp.id === 'number') {
          coveredPackageIds.add(dp.id);
        }
      }
    })
  );

  const orphanPackages = allProjectPackages.filter(
    (dp) => dp.id !== undefined && dp.id !== null && !coveredPackageIds.has(dp.id as number)
  );

  return [
    ...publicationDescriptors,
    ...orphanPackages.map((dp) => ({ type: 'dataPackage' as NodeType, data: dp, isFullyLoaded: false })),
  ];
}

function publicationInDataDescriptorsSortKey(p: PublicationDto): string {
  return (p.doi?.trim() || p.title?.trim() || `publication-${p.id}`).toLowerCase();
}

/**
 * Configuration for different node types.
 * Each type has its own configuration for loading and displaying data.
 */
export const NODE_TYPES: NodeTypeConfigMap = {
  // ===================================
  // Sample
  // ===================================
  sample: {
    calculateInitialExpandable: sampleInitialExpandable,
    canBeSelected: true,
    canHaveNotes: true,
    canHaveFiles: true,
    getLabel: (data: SampleDto) => {
      const pendingMarker = data.status === StatusDto.Pending ? '* ' : '';
      return pendingMarker + (data.sampleLabel || `Sample ${data.id}`);
    },
    getTypeLabel: () => 'Sample',
    /**
     * Live sample editor only. Published aggregates use `sampleAggregate`.
     */
    loadChildren: async (dataId: string, data: SampleDto, backend: IBackend, context: LoadContext) =>
      loadGrowthAwareSampleChildren(dataId, data, backend, context, {
        activityChildType: 'sampleActivity',
        sampleChildType: 'sample',
        activitiesRouteKey: extractEntityIdFromDataId(dataId),
      }),
    loadNotes: async (dataId: string, backend: IBackend) => backend.sample.getNotes(extractEntityIdFromDataId(dataId)),
    loadFiles: async (dataId: string, backend: IBackend) =>
      backend.sample.getFileGroups(extractEntityIdFromDataId(dataId)),
  },
  // ===================================
  // Sample Activity
  // ===================================
  sampleActivity: buildSampleActivityConfig({
    showPendingMarker: true,
    childSampleType: 'sample',
    childActivityType: 'sampleActivity',
    pieces: 'liveByLabel',
  }),
  // ===================================
  // Communication
  // ===================================
  communication: {
    calculateInitialExpandable: () => false,
    canBeSelected: true,
    canHaveNotes: false,
    canHaveFiles: true,
    getTypeLabel: () => 'Communication',
    getLabel: (data: CommunicationDto) => data.title || `Communication ${data.id}`,
    loadChildren: async (_dataId: string, _data: CommunicationDto, _backend: IBackend, _context: LoadContext) => [],
    loadNotes: async () => [], // Communication doesn't have notes
    loadFiles: async (dataId: string, backend: IBackend) => {
      const communicationId = extractNumericEntityIdFromDataId(dataId);
      return backend.communication.getFileGroups(communicationId);
    },
  },
  // ===================================
  // Project
  // ===================================
  project: {
    canBeSelected: true,
    canHaveNotes: true,
    canHaveFiles: false,
    getTypeLabel: () => 'Project',
    getLabel: (data: ProjectDto) => data.projectLabel || `Project ${data.id}`,
    loadChildren: async (dataId: string, data: ProjectDto, backend: IBackend, context: LoadContext) => {
      const projectId = extractEntityIdFromDataId(dataId);
      if (!projectId) throw new Error('Invalid nodeId for project, cannot extract project ID');
      const isDataRouteRootMode = Boolean(
        (context.userSettings as Record<string, unknown> | undefined)?.[DATA_ROUTE_ROOT_MODE_KEY]
      );
      if (isDataRouteRootMode) {
        return await loadDescriptorsForCanonicalDataBrowseProject(projectId, backend);
      }
      const activities = await backend.project.getProjectActivities(projectId);

      const children = [
        ...activities.map((activity) => {
          if (activity.id === undefined) throw new Error('Missing id for activity');
          return { type: 'projectActivity' as NodeType, data: activity };
        }),
      ] as NodeDescriptor<NodeType>[];

      if (isExternalProject(data)) {
        children.push({ type: 'communications' as NodeType, data: data });
      }

      // Simulations for theory projects, or when the viewer can see existing simulations
      if (showsSimulations(data)) {
        children.push({ type: 'simulations' as NodeType, data: data });
      }

      // Sample-related nodes for experimental projects, or when the viewer can see existing samples/shipments
      if (showsSamples(data)) {
        children.push({ type: 'shippings' as NodeType, data: data });
        children.push({ type: 'newSamples' as NodeType, data: data });
        if (!!data.oldSampleCount) {
          children.push({ type: 'oldSamples' as NodeType, data: data });
        }
      }
      return children;
    },
    loadNotes: async (dataId: string, backend: IBackend) => backend.project.getNotes(extractEntityIdFromDataId(dataId)),
    loadFiles: async () => [], // Project node has no file list API on this tree
  },
  // ===================================
  // Communications (container)
  // ===================================
  communications: {
    calculateInitialExpandable: (data) => !!data.communicationCount,
    canBeSelected: false,
    canHaveNotes: false,
    canHaveFiles: false,
    getTypeLabel: () => 'Communications',
    getLabel: () => 'Communications',
    loadChildren: async (dataId: string, _data: ProjectDto, backend: IBackend, _context: LoadContext) => {
      const projectId = extractEntityIdFromDataId(dataId);
      if (!projectId) throw new Error('Invalid nodeId for project, cannot extract project ID');
      const communications = await backend.project.getCommunications(projectId);

      return communications.map((communication) => {
        if (!communication.id) throw new Error('Missing id for communication');
        return { type: 'communication' as NodeType, data: communication };
      });
    },
    loadNotes: async () => [],
    loadFiles: async () => [],
  },
  // ===================================
  // Shippings (container)
  // ===================================
  shippings: {
    calculateInitialExpandable: (data) => !!data.shippingSampleCount,
    canBeSelected: false,
    canHaveNotes: false,
    canHaveFiles: false,
    getTypeLabel: () => 'Shippings',
    getLabel: () => 'Shipments',
    loadChildren: async (dataId: string, _data: ProjectDto, backend: IBackend, _context: LoadContext) => {
      const projectId = extractEntityIdFromDataId(dataId);
      if (!projectId) throw new Error('Invalid nodeId for project, cannot extract project ID');
      const shipments = await backend.project.getShippings(projectId);

      return shipments.map((shipment) => {
        if (!shipment.id) throw new Error('Missing id for shipment');
        return { type: 'shipping' as NodeType, data: shipment, isFullyLoaded: false };
      });
    },
    loadNotes: async () => [],
    loadFiles: async () => [],
  },
  // ===================================
  // Shipping
  // ===================================
  shipping: {
    canBeSelected: true,
    canHaveNotes: false,
    canHaveFiles: true,
    getTypeLabel: () => 'Shipping',
    getLabel: (data: ShippingDto) => {
      if (data.localTransfer) {
        return data.date
          ? `Transferred ${new Date(data.date).toLocaleDateString()}`
          : `Transfer initiated ${formatDate(data.lastChangedDate)}`;
      } else {
        return data.date
          ? `Shipped ${new Date(data.date).toLocaleDateString()}`
          : `Shipping initiated ${formatDate(data.lastChangedDate)}`;
      }
    },
    loadChildren: async (dataId: string, _data: ShippingDto, backend: IBackend) => {
      const shippingId = extractNumericEntityIdFromDataId(dataId);
      if (!shippingId) throw new Error('Invalid nodeId for shipping, cannot extract shipping ID');
      const samples = await backend.shipping.getSamples(shippingId);

      return samples.map((sample) => ({
        type: 'sample' as NodeType,
        data: sample,
      }));
    },
    loadNotes: async () => [],
    loadFiles: async () => [],
  },
  // ===================================
  // Old Samples (container)
  // ===================================
  oldSamples: {
    calculateInitialExpandable: (data) => !!data.oldSampleCount,
    canBeSelected: false,
    canHaveNotes: false,
    canHaveFiles: false,
    getLabel: (_data: any, userSettings?: UserSettingsDto) => {
      const nbOfRecentDays = userSettings?.nbOfRecentDays || 30;
      return `Samples older than ${nbOfRecentDays} days`;
    },
    loadChildren: async (dataId: string, _data: ProjectDto, backend: IBackend, context: LoadContext) => {
      const projectId = extractEntityIdFromDataId(dataId);
      if (!projectId) throw new Error('Invalid nodeId for project, cannot extract project ID');
      const nbOfRecentDays = context.userSettings?.nbOfRecentDays || 30;
      const samples = await backend.project.getSamples(
        projectId,
        false,
        nbOfRecentDays,
        context.userSettings?.showShippedSamples
      );

      // Check if grouping is needed - old samples can span years, so grouping always makes sense
      const sortNewestFirst = context.userSettings?.sortNewestSampleFirst ?? true;
      const groups = groupSamplesByDate(samples, 50, 100, sortNewestFirst);
      if (groups.length > 0) {
        // Return group nodes instead of individual samples
        return groups.map((group) => ({
          type: (group.type === 'year' ? 'sampleYearGroup' : 'sampleQuarterGroup') as NodeType,
          data: group,
          isFullyLoaded: true, // Group metadata is complete
        }));
      }

      // Small dataset - return samples directly
      return samples.map((sample) => {
        if (!sample.sampleLabel) throw new Error('Missing sampleLabel for sample');
        return { type: 'sample' as NodeType, data: sample, isFullyLoaded: false };
      });
    },
    loadNotes: async () => [],
    loadFiles: async () => [],
  },
  // ===================================
  // New Samples (container)
  // ===================================
  newSamples: {
    calculateInitialExpandable: (data) => !!data.recentSampleCount,
    canBeSelected: false,
    canHaveNotes: false,
    canHaveFiles: false,
    getLabel: (_data: any, userSettings?: UserSettingsDto) => {
      const nbOfRecentDays = userSettings?.nbOfRecentDays || 30;
      return `Recent Samples (${nbOfRecentDays} days)`;
    },
    loadChildren: async (dataId: string, _data: ProjectDto, backend: IBackend, context: LoadContext) => {
      const projectId = extractEntityIdFromDataId(dataId);
      if (!projectId) throw new Error('Invalid nodeId for project, cannot extract project ID');
      const nbOfRecentDays = context.userSettings?.nbOfRecentDays || 30;
      const samples = await backend.project.getSamples(
        projectId,
        true,
        nbOfRecentDays,
        context.userSettings?.showShippedSamples
      );

      // Check if grouping is needed for recent samples - only group if recent days setting is large enough (≥90)
      if (nbOfRecentDays >= 90) {
        const sortNewestFirst = context.userSettings?.sortNewestSampleFirst ?? true;
        const groups = groupSamplesByDate(samples, 50, 100, sortNewestFirst);
        if (groups.length > 0) {
          // Return group nodes instead of individual samples
          return groups.map((group) => ({
            type: (group.type === 'year' ? 'sampleYearGroup' : 'sampleQuarterGroup') as NodeType,
            data: group,
            isFullyLoaded: true, // Group metadata is complete
          }));
        }
      }

      // Small dataset - return samples directly
      return samples.map((sample) => {
        if (!sample.sampleLabel) throw new Error('Missing sampleLabel for sample');
        return { type: 'sample' as NodeType, data: sample, isFullyLoaded: false };
      });
    },
    loadNotes: async () => [],
    loadFiles: async () => [],
  },
  // ===================================
  // Publication
  // ===================================
  publication: {
    // Matches server PublicationMapper.HasData (linked data-package rows).
    calculateInitialExpandable: (data: PublicationDto) => Boolean(data.hasData),
    canBeSelected: true,
    canHaveNotes: false,
    canHaveFiles: false,
    getTypeLabel: () => 'Publication',
    getLabel: (data: PublicationDto) => data.doi?.trim() || data.title || `Publication ${data.id}`,
    loadChildren: async (_dataId: string, data: PublicationDto, backend: IBackend, context: LoadContext) => {
      const isDataRouteBrowse = Boolean(
        (context.userSettings as Record<string, unknown> | undefined)?.[DATA_ROUTE_ROOT_MODE_KEY]
      );
      const nestedUnderCanonicalProjectBrowse =
        context.parentType === 'project' || context.parentType === 'projectInData';
      const nestedUnderPublicationActivity = context.parentType === 'projectActivity';

      const shouldLoadLinkedDataPackages =
        nestedUnderPublicationActivity || (isDataRouteBrowse && (context.isRoot || nestedUnderCanonicalProjectBrowse));

      if (!shouldLoadLinkedDataPackages) {
        return [];
      }

      const idOrDoi = data.doi?.trim() || String(data.id);
      const dataPackages = await backend.publication.getDataPackagesByIdOrDoi(idOrDoi);
      return dataPackages.map((dp) => ({ type: 'dataPackage' as NodeType, data: dp, isFullyLoaded: false }));
    },
    loadNotes: async () => [],
    loadFiles: async () => [],
  },
  // ===================================
  // Data Package
  // ===================================
  dataPackage: {
    canBeSelected: true,
    canHaveNotes: true,
    canHaveFiles: true,
    getLabel: (data: DataPackageDto) => data.label || `Data Package ${data.id}`,
    loadChildren: async (dataId: string, data: DataPackageDto, backend: IBackend, _context: LoadContext) => {
      const apiId = getDataPackageApiId(dataId);
      const [samples, activities] = await Promise.all([
        backend.data.getSamples(apiId),
        backend.data.getActivities(apiId),
      ]);
      const sampleChildType = data.isSnapshot ? ('sampleSnapshot' as NodeType) : ('sampleInPackage' as NodeType);

      return [
        ...samples.map((s) => ({ type: sampleChildType, data: s })),
        ...activities.map((a) => ({
          type: 'theoryActivityInPackage' as NodeType,
          data: a,
        })),
      ];
    },
    loadNotes: async (dataId: string, backend: IBackend) => backend.data.getNotes(extractEntityIdFromDataId(dataId)),
    loadFiles: async (dataId: string, backend: IBackend) =>
      backend.data.getFileGroups(extractEntityIdFromDataId(dataId)),
  },
  // ===================================
  // Project Activity
  // ===================================
  projectActivity: {
    calculateInitialExpandable: (data: ProjectActivityDto) => data?.type === ActivityTypeDto.Publication,
    canBeSelected: true,
    canHaveNotes: true,
    canHaveFiles: true,
    getTypeLabel: (activity?: ProjectActivityDto) =>
      activity ? getActivityTypeDisplay(activity.type) : 'Project Activity',
    getLabel: (data: ProjectActivityDto) => {
      const pendingMarker = data.status === StatusDto.Pending ? '* ' : '';
      if (data.type === ActivityTypeDto.Publication) {
        const year = data.publication?.year ? String(data.publication.year) : undefined;
        const journal = data.publication?.journal?.trim() || undefined;
        if (year && journal) return pendingMarker + `${year} - ${journal}`;
        if (journal) return pendingMarker + journal;
        if (year) return pendingMarker + year;
      }
      const type = getActivityTypeDisplay(data.type);
      return pendingMarker + `${type} - ${data.date ? new Date(data.date).toLocaleDateString() : 'No date'}`;
    },
    loadChildren: async (_dataId: string, data: ProjectActivityDto, _backend: IBackend, _context: LoadContext) => {
      if (data?.type === ActivityTypeDto.Publication && data.publication) {
        return [{ type: 'publication' as NodeType, data: data.publication }];
      }
      return [];
    },
    loadNotes: async (dataId: string, backend: IBackend) =>
      backend.projectActivity.getNotes(extractNumericEntityIdFromDataId(dataId)),
    loadFiles: async (dataId: string, backend: IBackend) =>
      backend.projectActivity.getFileGroups(extractNumericEntityIdFromDataId(dataId)),
  },
  // ===================================
  // Theory Activity
  // ===================================
  theoryActivity: {
    calculateInitialExpandable: () => false,
    canBeSelected: true,
    canHaveNotes: true,
    canHaveFiles: true,
    getTypeLabel: () => 'Theory',
    getLabel: (data: TheoryActivityDto) =>
      `${data.desc} - ${data.date ? new Date(data.date).toLocaleDateString() : 'No date'}`,
    loadChildren: async (_dataId: string, _data: TheoryActivityDto, _backend: IBackend, _context: LoadContext) => [],
    loadNotes: async (dataId: string, backend: IBackend) =>
      backend.theoryActivity.getNotes(extractNumericEntityIdFromDataId(dataId)),
    loadFiles: async (dataId: string, backend: IBackend) =>
      backend.theoryActivity.getFileGroups(extractNumericEntityIdFromDataId(dataId)),
  },
  // ===================================
  // Simulations (container)
  // ===================================
  simulations: {
    canBeSelected: false,
    canHaveNotes: false,
    canHaveFiles: false,
    getTypeLabel: () => 'Simulations',
    getLabel: () => 'Simulations',
    loadChildren: async (dataId: string, _data: ProjectDto, backend: IBackend, _context: LoadContext) => {
      const projectId = extractEntityIdFromDataId(dataId);
      if (!projectId) throw new Error('Invalid nodeId for project, cannot extract project ID');

      // Load the simulation packages of the project
      const dataPackages = await backend.project.getDataPackages(projectId, true);
      return dataPackages.map((dataPackage) => ({ type: 'simulationDataset' as NodeType, data: dataPackage }));
    },
    loadNotes: async () => [],
    loadFiles: async () => [],
  },
  // ===================================
  // Simulation Dataset
  // ===================================
  simulationDataset: {
    canBeSelected: true,
    canHaveNotes: true,
    canHaveFiles: true,
    getTypeLabel: () => 'Simulation Dataset',
    getLabel: (data: DataPackageDto) => data.label || `Simulation Dataset ${data.id}`,
    loadChildren: async (dataId: string, _data: DataPackageDto, backend: IBackend, _context: LoadContext) => {
      const apiId = getDataPackageApiId(dataId);
      const theoryActivities = await backend.data.getActivities(apiId);

      return theoryActivities.map((activity) => ({ type: 'theoryActivity' as NodeType, data: activity }));
    },
    loadNotes: async (dataId: string, backend: IBackend) => backend.data.getNotes(extractEntityIdFromDataId(dataId)),
    loadFiles: async (dataId: string, backend: IBackend) =>
      backend.data.getFileGroups(extractEntityIdFromDataId(dataId)),
  },

  // ===================================
  // Sample Group Nodes (UI Grouping)
  // ===================================
  sampleYearGroup: {
    calculateInitialExpandable: () => true,
    canBeSelected: false,
    canHaveNotes: false,
    canHaveFiles: false,
    getTypeLabel: () => 'Sample Group',
    getLabel: (data: any) => data.label || `${data.year} (${data.count} samples)`,
    loadChildren: async (_dataId: string, groupData: any, _backend: IBackend, _context: LoadContext) => {
      // Extract grouping info from the group data
      const { samples } = groupData;

      // Return sample descriptors (TreeNodeFactory will create the actual nodes)
      return samples.map((sample: any) => ({
        type: 'sample' as NodeType,
        data: sample,
        isFullyLoaded: false, // Samples might need data reload when selected
      }));
    },
    loadNotes: async () => [],
    loadFiles: async () => [],
  },

  sampleQuarterGroup: {
    calculateInitialExpandable: () => true,
    canBeSelected: false,
    canHaveNotes: false,
    canHaveFiles: false,
    getTypeLabel: () => 'Sample Group',
    getLabel: (data: any) => data.label || `${data.quarter} (${data.count} samples)`,
    loadChildren: async (_dataId: string, groupData: any, _backend: IBackend, _context: LoadContext) => {
      const { samples } = groupData;

      return samples.map((sample: any) => ({
        type: 'sample' as NodeType,
        data: sample,
        isFullyLoaded: false,
      }));
    },
    loadNotes: async () => [],
    loadFiles: async () => [],
  },

  // ===================================
  // Published aggregate (/samples merge — not a writable row)
  // ===================================
  sampleAggregate: buildReadOnlySampleConfig({
    typeLabel: 'Sample',
    loadChildren: async (dataId, data, backend, context) => {
      if (data.id == null) throw new Error('sampleAggregate requires numeric sample id');
      return loadGrowthAwareSampleChildren(dataId, data, backend, context, {
        activityChildType: 'sampleActivityAggregate',
        sampleChildType: 'sampleAggregate',
        activitiesRouteKey: String(data.id),
      });
    },
    loadNotes: loadSampleNotesViaSampleApi,
    loadFiles: loadSampleFilesViaSampleApi,
  }),

  // ===================================
  // Read-Only Node Types for Data Packages
  // ===================================

  // Live sample under a live data package (unlink only; no field edits)
  sampleInPackage: buildReadOnlySampleConfig({
    typeLabel: 'Sample',
    loadChildren: async (dataId, _data, backend) => {
      const entityId = extractEntityIdFromDataId(dataId);
      if (!entityId) throw new Error('Invalid nodeId for sample, cannot extract sample ID');
      const activities = await backend.sample.getActivities(entityId, undefined, undefined);
      return mapActivitiesToDescriptors(activities, 'sampleActivityInPackage');
    },
    loadNotes: loadSampleNotesViaSampleApi,
    loadFiles: loadSampleFilesViaSampleApi,
  }),

  // Sample Snapshot (in data packages)
  sampleSnapshot: buildReadOnlySampleConfig({
    typeLabel: 'Sample Snapshot',
    loadChildren: async (dataId, _data, backend) => {
      const numericId = extractNumericEntityIdFromDataId(dataId);
      const activities = await backend.sampleSnapshot.getActivities(numericId, undefined, undefined);
      return mapActivitiesToDescriptors(activities, 'sampleActivitySnapshot');
    },
    loadNotes: async (dataId, backend) => backend.sampleSnapshot.getNotes(extractNumericEntityIdFromDataId(dataId)),
    loadFiles: async (dataId, backend) =>
      backend.sampleSnapshot.getFileGroups(extractNumericEntityIdFromDataId(dataId)),
  }),

  // Sample Activity under a live data package (browse + unlink is on the sample)
  sampleActivityInPackage: buildSampleActivityConfig({
    isReadOnly: true,
    showPendingMarker: false,
    childSampleType: 'sampleInPackage',
    childActivityType: 'sampleActivityInPackage',
    pieces: 'liveByLabel',
  }),

  // Snapshot-package activity copy — pending remove/trim via the owning data package
  sampleActivitySnapshot: buildSampleActivityConfig({
    isReadOnly: true,
    showPendingMarker: false,
    childSampleType: 'sampleSnapshot',
    childActivityType: 'sampleActivitySnapshot',
    pieces: 'snapshot',
  }),

  // Merged activity under a published /samples aggregate — view only (no package remove).
  sampleActivityAggregate: buildSampleActivityConfig({
    isReadOnly: true,
    showPendingMarker: false,
    childSampleType: 'sampleAggregate',
    childActivityType: 'sampleActivityAggregate',
    pieces: 'liveByLabel',
  }),

  // Theory Activity Read-Only (in data packages)
  theoryActivityInPackage: {
    canBeSelected: true,
    isReadOnly: true,
    canHaveNotes: true, // Can view notes
    canHaveFiles: true, // Can view files
    getTypeLabel: () => 'Theory',
    getLabel: (data: TheoryActivityDto) =>
      `${data.desc} - ${data.date ? new Date(data.date).toLocaleDateString() : 'No date'}`,
    loadChildren: async () => [], // No children for theory activities
    loadNotes: async (dataId: string, backend: IBackend) => {
      const activityId = extractNumericEntityIdFromDataId(dataId);
      return backend.theoryActivity.getNotes(activityId);
    },
    loadFiles: async (dataId: string, backend: IBackend) => {
      const activityId = extractNumericEntityIdFromDataId(dataId);
      return backend.theoryActivity.getFileGroups(activityId);
    },
  },

  // Project Read-Only (in data packages)
  projectInData: {
    canBeSelected: true,
    isReadOnly: true,
    canHaveNotes: true, // Can view notes
    canHaveFiles: false, // Projects don't have files
    getTypeLabel: () => 'Project',
    getLabel: (data: ProjectDto) => data.projectLabel || `Project ${data.id}`,
    loadChildren: async (dataId: string, _data: ProjectDto, backend: IBackend, context: LoadContext) => {
      const isDataRouteRootMode = Boolean(
        (context.userSettings as Record<string, unknown> | undefined)?.[DATA_ROUTE_ROOT_MODE_KEY]
      );
      if (isDataRouteRootMode && context.isRoot) {
        const projectId = extractEntityIdFromDataId(dataId);
        if (!projectId) throw new Error('Invalid nodeId for projectInData, cannot extract project ID');
        const dataPackages = await backend.project.getDataPackages(projectId, false);
        return dataPackages.map((dp) => ({ type: 'dataPackage' as NodeType, data: dp, isFullyLoaded: false }));
      }
      // Embedded references in a normal project tree remain leaf nodes.
      return [];
    },
    loadNotes: async (dataId: string, backend: IBackend) => {
      const projectId = extractEntityIdFromDataId(dataId);
      return backend.project.getNotes(projectId);
    },
    loadFiles: async () => [], // Projects don't have files
  },

  // Publication Read-Only (in data packages)
  publicationInData: {
    calculateInitialExpandable: (data: PublicationDto) => Boolean(data.hasData),
    canBeSelected: true,
    isReadOnly: true,
    canHaveNotes: false, // Publications don't have notes
    canHaveFiles: false, // Publications don't have files
    getTypeLabel: () => 'Publication',
    getLabel: (data: PublicationDto) => data.doi?.trim() || data.title || `Publication ${data.id}`,
    loadChildren: async (_dataId: string, data: PublicationDto, backend: IBackend, context: LoadContext) => {
      const isDataRouteBrowse = Boolean(
        (context.userSettings as Record<string, unknown> | undefined)?.[DATA_ROUTE_ROOT_MODE_KEY]
      );
      const nestedUnderCanonicalProjectBrowse =
        context.parentType === 'project' || context.parentType === 'projectInData';
      const nestedUnderPublicationActivity = context.parentType === 'projectActivity';

      // Canonical `/data` browse, or editable project-tree publication activities.
      const shouldLoadLinkedDataPackages =
        nestedUnderPublicationActivity || (isDataRouteBrowse && (context.isRoot || nestedUnderCanonicalProjectBrowse));

      if (!shouldLoadLinkedDataPackages) {
        return [];
      }

      const idOrDoi = data.doi?.trim() || String(data.id);
      const dataPackages = await backend.publication.getDataPackagesByIdOrDoi(idOrDoi);
      return dataPackages.map((dp) => ({ type: 'dataPackage' as NodeType, data: dp, isFullyLoaded: false }));
    },
    loadNotes: async () => [],
    loadFiles: async () => [],
  },
};

/**
 * Grouping utility functions for large datasets
 */

interface SampleGroup {
  type: 'year' | 'quarter';
  id: string; // Required for TreeNodeFactory
  label: string;
  year?: number;
  quarter?: string;
  samples: any[];
  count: number;
}

/**
 * Groups samples by year, with quarterly subdivision if needed
 */
export function groupSamplesByDate(
  samples: any[],
  minSamplesForYearGrouping: number = 50,
  minSamplesPerYearForQuarters: number = 100,
  sortNewestFirst: boolean = true
): SampleGroup[] {
  if (samples.length <= minSamplesForYearGrouping) {
    // Small enough - no grouping needed
    return [];
  }

  // Group by year first
  const yearGroups = new Map<number, any[]>();
  for (const sample of samples) {
    const sampleDate = new Date(sample.creationDate || sample.date || sample.lastChangedDate);
    const year = sampleDate.getFullYear();

    if (!yearGroups.has(year)) {
      yearGroups.set(year, []);
    }
    yearGroups.get(year)!.push(sample);
  }

  // Check if further subdivision needed
  const result: SampleGroup[] = [];
  for (const [year, yearSamples] of yearGroups) {
    if (yearSamples.length <= minSamplesPerYearForQuarters) {
      // Year group doesn't need quarter subdivision
      result.push({
        type: 'year',
        id: `year-${year}`, // Add required id property
        label: `${year} (${yearSamples.length} samples)`,
        year,
        samples: yearSamples,
        count: yearSamples.length,
      });
    } else {
      // Subdivide by quarter - year has too many samples
      const quarterGroups = groupSamplesByQuarter(yearSamples, year);
      result.push(...quarterGroups);
    }
  }

  // Simple alphabetical sort - "2023 Q1" format sorts naturally
  return result.sort((a, b) => {
    const labelA = a.quarter || `${a.year}`;
    const labelB = b.quarter || `${b.year}`;
    return sortNewestFirst ? labelB.localeCompare(labelA) : labelA.localeCompare(labelB);
  });
}

/**
 * Groups samples by quarter within a year
 */
function groupSamplesByQuarter(samples: any[], year: number): SampleGroup[] {
  const quarterGroups = new Map<string, any[]>();

  for (const sample of samples) {
    const sampleDate = new Date(sample.creationDate || sample.date || sample.lastChangedDate);
    const quarter = Math.ceil((sampleDate.getMonth() + 1) / 3);
    const quarterKey = `${year} Q${quarter}`;

    if (!quarterGroups.has(quarterKey)) {
      quarterGroups.set(quarterKey, []);
    }
    quarterGroups.get(quarterKey)!.push(sample);
  }

  return Array.from(quarterGroups.entries()).map(([quarter, samples]) => ({
    type: 'quarter' as const,
    id: `quarter-${quarter.replace(' ', '-')}`, // Add required id property
    label: `${quarter} (${samples.length} samples)`,
    year,
    quarter,
    samples,
    count: samples.length,
  }));
}

/**
 * Helper function to check if a node type supports files
 */
export function nodeTypeSupportsFiles(nodeType: NodeType): boolean {
  return NODE_TYPES[nodeType]?.canHaveFiles ?? false;
}

/**
 * Helper function to check if a node type supports notes
 */
export function nodeTypeSupportsNotes(nodeType: NodeType): boolean {
  return NODE_TYPES[nodeType]?.canHaveNotes ?? false;
}

export function nodeTypeIsReadonly(nodeType: NodeType): boolean {
  return NODE_TYPES[nodeType]?.isReadOnly ?? false;
}

/**
 * Whether the UI must treat this node as non-editable (no field saves, no Plus add menus).
 */
export function nodeIsReadOnlyForUi(nodeType: NodeType): boolean {
  return nodeTypeIsReadonly(nodeType);
}
